import React, { useState, useEffect, useCallback, memo } from 'react';
import { Modal } from 'react-bootstrap';
import axios from '../axios';
import LoadingModal from './LoadingModal';
import { SelectedCustomer } from '../types/CustomerDocumentEdit';
import { KWANKGM10090A03 } from '../const/api_modules';
import { customer_select_max_line as max_line } from '../const/CustomerDocumentEdit';

interface Props {
  show: boolean;
  handleCancel: VoidFunction;
  handleApply: (customer_list: SelectedCustomer[]) => void;
}

interface ListItem {
  id: string;
  name: string;
  email: string;
}

const ModalCustomerSelect: React.FC<Props> = ({
  show,
  handleCancel,
  handleApply,
}) => {
  // 全結果
  const [result_list, setResultList] = useState<SelectedCustomer[]>([]);
  // 表示中のページ
  const [now_page, setNowPage] = useState(1);
  // 件数ナビゲーション
  const [paging_nav, setPagingNav] = useState({
    start: 0,
    start_label: 0,
    end: 0,
    end_label: 0,
    max_page: 0,
    visible: false,
  });
  // ローディング
  const [is_loading, setIsLoading] = useState(false);
  // 検索文字列
  const [search_string, setSearchString] = useState('');

  // ページングナビゲーションの表示制御
  useEffect(() => {
    const scanning_index = decisionScanningIndex();

    const new_paging_nav = {...paging_nav};
    new_paging_nav.start = scanning_index.start;
    new_paging_nav.start_label = scanning_index.start + 1;
    new_paging_nav.end = scanning_index.end;
    new_paging_nav.end_label = scanning_index.end;
    new_paging_nav.max_page = result_list.length;

    if (result_list.length > 0) {
      new_paging_nav.visible = true;
    } else {
      new_paging_nav.visible = false;
    }

    setPagingNav(new_paging_nav);
  }, [result_list, now_page]);

  // 結果リストを生成する
  const jsxResultList = useCallback(() => {
    const list: JSX.Element[] = [];
    const scanning_index = decisionScanningIndex();

    // クリックイベント
    const handleChangeResultListItem = (index: number) => {
      const new_result_list = [...result_list];
      new_result_list[index].checked = !new_result_list[index].checked;
      setResultList(new_result_list);
    };

    for (let i = scanning_index.start; i < scanning_index.end; i++) {
      list.push(
        <td className="table-td" key={`result-list-${i}`}>
          <input type="checkbox" name="name" value="2" checked={result_list[i].checked} onChange={() => { handleChangeResultListItem(i) }} />
          <span>　</span>
          {result_list[i].name}
          <span>　</span>
          <span style={{color: "#6c6e71"}}>{result_list[i].email}</span>
        </td>
      );
    }

    return list;
  }, [result_list, now_page]);

  // ページングを生成する
  const jsxPaging = () => {
    // 総ページ数
    const max_page = result_list.length > 0 ? Math.ceil(result_list.length / max_line) : 1;
    // JSX.Element
    const elements: JSX.Element[] = [];
    // アクティブページ切り替え処理
    const switchActive = (page: number) => {
      if (page === now_page) {
        return true;
      }
      return false;
    }
    // クリックイベント
    const handleClickPagingTargetPage = (page: number) => {
      setNowPage(page);
    }

    for (let i = 1; i <= max_page; i++) {
      elements.push(
        <li data-test="page-item" className={`${switchActive(i) && 'active'} page-item`} key={`paging-${i}`}>
          <a data-test="page-link" className="page-link page-link" onClick={() => { handleClickPagingTargetPage(i) }}>{i}</a>
        </li>
      );
    }

    return elements;
  };

  // ページングの前次ボタン押下時の処理
  // 前
  const handleClickPagingPrev = () => {
    if (now_page > 1) {
      setNowPage(prev => prev - 1);
    }
  };
  // 次
  const handleClickPagingNext = () => {
    const max_page = Math.ceil(result_list.length / max_line);
    if (now_page < max_page) {
      setNowPage(prev => prev + 1);
    }
  }

  // 全選択ボタン押下時の処理
  const handleClickSelectAll = () => {
    const new_result_list = [...result_list];
    const scannig_index = decisionScanningIndex();

    // オフセットの範囲内で全ての要素が選択済みであるかチェックする
    let is_all_selected = true; // オフセット範囲内の全ての項目が選択状態である
    for (let i = scannig_index.start; i < scannig_index.end; i++) {
      if (!new_result_list[i].checked) {
        is_all_selected = false;
      }
    }

    // オフセットの範囲内の全ての要素のチェック状態を操作する
    for (let i = scannig_index.start; i < scannig_index.end; i++) {
      if (!is_all_selected) {
        new_result_list[i].checked = true;
      } else {
        new_result_list[i].checked = false;
      }
    }

    setResultList(new_result_list);
  };

  // 検索文字列の変更処理
  const handleChangeSearchString = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchString(event.target.value);
  };

  // 検索ボタン押下時の処理
  const handleClickSearch = async () => {
    try {
      setIsLoading(true);

      const response = await axios.post(`${location.origin}${process.env.MIX_MOCK_URL}/api/common`, {
        reception_id: '0',
        reception_cd: null,
        module_name: KWANKGM10090A03,
        is_json: true,
        search_text: {
          val: search_string,
        }
      });

      if (!response.data.result) {
        throw(response);
      }

      const new_result_list: SelectedCustomer[] = [];

      // 選択状態プロパティを初期化した状態で結果リストを生成する
      response.data.detail[0].value.map((item: ListItem) => {
        new_result_list.push({
          id: item.id,
          name: item.name,
          email: item.email,
          checked: false,
        });
      });

      setResultList(new_result_list);
    } catch (error) {
      console.error(error.response.data.message);
      alert(error.response.data.message);
    } finally {
      setIsLoading(false);
    }
  };

  // 適用ボタン押下時の処理
  const handleClickApply = () => {
    const res_list: SelectedCustomer[] = [];
    if (result_list.length > 0) {
      result_list.map(item => {
        if (item.checked) {
          res_list.push(item);
        }
      });
    }

    handleApply(res_list);
    handleCancel();
  };

  // result_listを走査する時の開始インデックス、終了インデックスを決定する
  const decisionScanningIndex = () => {
    let start_index = 0;
    let end_index = max_line;

    // 結果リストが0件の場合
    if (result_list.length === 0) {
      return {
        start: 0,
        end: 0,
      }
    }

    // 総ページ数
    const max_page = Math.ceil(result_list.length / max_line);

    // ページ数により開始位置を特定する
    if (now_page > 1 && now_page === max_page) {
      // 最終ページの場合
      start_index = (now_page - 1) * max_line;
      end_index = result_list.length;
    } else if (now_page > 1) {
      // 最終ページではない場合
      start_index = (now_page - 1) * max_line;
      end_index = start_index + max_line;
    } else {
      // 1ページ目の場合
      if (result_list.length < max_line) {
        end_index = result_list.length
      }
    }

    return {
      start: start_index,
      end: end_index,
    };
  };

  // 結果リストを表示する
  const visibleResultList = () => {
    // 結果リストが0件の場合はリストを表示しない
    if (result_list.length === 0) {
      return <></>;
    }
    return(
      <table className="p-form-table">
        <tbody>
          <tr className="p-form-table-box js-search-param2">
            {jsxResultList()}
          </tr>
        </tbody>
      </table>
    );
  };

  return (
    <Modal
      className="d-flex justify-content-center align-items-center modal-item-select"
      show={show}
      animation={false}
      onHide={() => {}}
    >
      <div className="modal__inner">
        <div className="modal__contents">
          <img className="modal2-close modal__close js-img-switch js-fade-in" src="/img/ico_012_sp.png" alt="close" onClick={handleCancel} />
          <div className="p-heading3-box">
            <h3 className="heading3">顧客選択</h3>
          </div>
          <div className="p-lead modal_wrapper">
            <div className="modal_input">
              <label className="modal_label">顧客名またはメールアドレス</label>
              <br />
              <input className="c-input input_xs" type="text" value={search_string} onChange={handleChangeSearchString} />
            </div>
            <br />
            <div className="modal_input">
              <a className="c-btn btn-mini btn-green modal3-open modal_button" onClick={handleClickSearch}>検索</a>
            </div>
          </div>
          <div className="p-heading4-box">
            <h4 className="heading4 -mycar" style={{display: "flex"}}>
              <div className="col-md-2 vertical">
                <div className="after-margin">一覧</div>
                <a className="c-btn btn-supermini btn-green modal3-open modal_button all-select-button" onClick={handleClickSelectAll}>全選択</a>
              </div>
            </h4>
          </div>
          {visibleResultList()}
          <br />
          <div className="row">
            <div data-test="datatable-info" className="col-sm-12 col-md-5">
              <div className={`dataTables_info ${!paging_nav.visible && 'info-disabled'}`} role="status" aria-live="polite"> {paging_nav.start_label} ～ {paging_nav.end_label} 件目を表示 (全 {paging_nav.max_page} 件)</div>
            </div>
            <div data-test="datatable-pagination" className="col-sm-12 col-md-7">
              <div className="dataTables_paginate">
                <ul data-test="pagination" className="pagination">
                  <li data-test="page-item" className="page-item"><a data-test="page-link" aria-label="前" className="page-link page-link" onClick={handleClickPagingPrev}><span>前</span></a></li>
                  {jsxPaging()}
                  <li data-test="page-item" className="page-item"><a data-test="page-link" aria-label="次" className="page-link page-link" onClick={handleClickPagingNext}><span>次</span></a></li>
                </ul>
              </div>
            </div>
          </div>
          <br />
          <div className="modal_input">
            <br /> <a className="c-btn btn-mini btn-green modal3-open modal_button" onClick={handleClickApply}>適用</a>
          </div>
        </div>
      </div>
      <LoadingModal show={is_loading} />
    </Modal>
  );
};

export default memo(ModalCustomerSelect);